import React, { useState, useEffect, useMemo } from "react";
import { HeaderPage } from "../base/headerPage";
import { Label } from "@progress/kendo-react-labels";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { dummyContractTypeData, dummyActivityData, dummyExpiringContractsData } from "./dummyData";
import { Button } from "@progress/kendo-react-buttons";
import "./contractManagement.scss";
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartSeriesLabels,
  ChartLegend,
  ChartTooltip,
} from "@progress/kendo-react-charts";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { LoadingPanel } from "../../components/loadingPanel";
import { Window } from "@progress/kendo-react-dialogs";
import { useHistory } from "react-router-dom";
import { POSTAPI, GETAPI, generateHeader } from "../../api-setup/api-helper";
import { INSERT_CONTRACT_DETAILS_V2, GET_CONTRACT_OVERVIEW } from "../../api-setup/api-endpoints";
import { getUserCredential } from "../../lib/cookies";
import { useDispatch } from "react-redux";
import {
  GET_LOCATION_FOR_PROVIDER,
  GET_CONTRACT_FOR_PROVIDER,
  SELECT_MOBILE_USER_DETAILS,
} from "../../api-setup/api-endpoints";
import { fetchProviderId } from "./services/providerService";
import { fetchLocationForProvider } from "./services/locationService";
import { fetchContractTypeForProvider } from "./services/contractTypeService";
import { fetchProviderCount } from "./services/providerCountService";
import { fetchContractOverview } from "./services/contractOverviewService";
import { fetchDivisionData } from "./services/divisionService";
import { capitalize } from "./services/capitalize";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Skeleton } from "@progress/kendo-react-indicators";

const headerConfig = {
  title: "Contract Management",
  id: "contractManagement-grid",
};

const ContractManagement = (pageProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const cookiesData = getUserCredential();
  const mountedRef = React.useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // store provider id
  const [providerId, setProviderId] = useState(null);

  // Window state for contract overview
  const [isContractOverviewWindowOpen, setIsContractOverviewWindowOpen] = useState(false);

  // Action click handler
  const handleActionClick = (dataItem) => {
    return;
    // Navigate to contract management details page
    // history.push('/contract_management_details');
  };

  // Filter state management
  const [hospitalDataFields, setHospitalDataFields] = useState({
    hospitalData: [],
    hospitalDataValue: [{ text: "Select All", id: "Select All" }],
    filterValue: "",
    search: "",
    isHospitalDataLoading: false,
  });

  // Store original hospital data for filter reset
  const [originalHospitalData, setOriginalHospitalData] = useState([]);

  // Division filter state
  const [divisionDataFields, setDivisionDataFields] = useState({
    divisionData: [],
    divisionDataValue: [{ text: "Select All", id: "Select All" }],
    filterValue: "",
    search: "",
    isDivisionDataLoading: false,
  });

  // Store original division data for filter reset
  const [originalDivisionData, setOriginalDivisionData] = useState([]);

  const [contractTypeDataFields, setContractTypeDataFields] = useState({
    contractTypeData: [],
    contractTypeDataValue: [{ text: "Select All", id: "Select All" }],
    filterValue: "",
    search: "",
    isContractTypeDataLoading: true,
  });

  // Store original contract type data for filter reset
  const [originalContractTypeData, setOriginalContractTypeData] = useState([]);

  // Store raw contract type responses for overview data fetching
  const [contractTypeResponses, setContractTypeResponses] = useState([]);

  // Contract overview data state
  const [contractOverviewData, setContractOverviewData] = useState([]);
  const [filteredContractOverviewData, setFilteredContractOverviewData] = useState([]);
  const [isContractOverviewLoading, setIsContractOverviewLoading] = useState(false);

  // Location / Hospital Data state
  const [locationData, setLocationData] = useState([]);
  const [isLocationDataLoading, setIsLocationDataLoading] = useState(true);
  const [hospitalCount, setHospitalCount] = useState(0);
  const [contractTypesCount, setContractTypesCount] = useState(0);

  // Provider count state
  const [providerCount, setProviderCount] = useState(0);
  const [isProviderCountLoading, setIsProviderCountLoading] = useState(true);

  // Stats display delay state
  const [showStatsData, setShowStatsData] = useState(false);

  // Contract chart data state
  const [contractChartData, setContractChartData] = useState([]);

  // Filter change handler
  const handleChangeDropdownValue = (e, name, setDataFields, dataList, valueData, dataFields) => {
    if (valueData && valueData.filter((x) => x.text === "Select All").length > 0) {
      let index = e.value.findIndex((x) => x.text === "Select All");
      e.value.splice(index, 1);
    } else if (e.value && e.value.filter((x) => x.text === "Select All").length > 0 && e.value.length > 1) {
      e.value = [];
      e.value.push({ text: "Select All", id: "Select All" });
    }
    if (e.value.length === 0) {
      e.value.push({ text: "Select All", id: "Select All" });
    }

    handleOnChange(e.value, name, setDataFields, dataList, dataFields);
  };

  const handleOnChange = (e, name, setDataFields, dataList, dataFields) => {
    let dataValue = [];
    if (e.length === 1 && e.some((x) => x.text === "Select All")) {
      dataValue.push({ text: "Select All", id: "Select All" });
    } else {
      dataValue = [...e];
    }
    setDataFields({
      ...dataFields,
      [name]: dataValue,
    });
  };

  const filterChange = (event, setDataFields, moduleData, key) => {
    let dataValue = filterBy(moduleData.slice(), event.filter);
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
      filterValue: event.filter.value,
      search: event.filter,
    }));
  };

  const onOpenChange = (setDataFields, moduleData, key, dataFields, dataFieldsValue) => {
    let dataValue =
      dataFields.filterValue && dataFields.filterValue !== ""
        ? filterBy(dataFieldsValue.slice(), dataFields.search)
        : moduleData;
    setDataFields((prev) => ({
      ...prev,
      [`${key}`]: dataValue,
    }));
  };

  const handleBlurDropdown = (setDataFields) => {
    setDataFields((prev) => ({
      ...prev,
      filterValue: "",
      search: "",
    }));
  };

  // Apply filters to contract data based on selected division, hospital and contract type
  const applyFilters = () => {
    let filteredData = [...contractOverviewData];

    // Get selected divisions (excluding "Select All")
    const selectedDivisions = divisionDataFields.divisionDataValue
      .filter((item) => item.id !== "Select All")
      .map((item) => item.text);

    // Get selected hospitals (excluding "Select All")
    const selectedHospitals = hospitalDataFields.hospitalDataValue
      .filter((item) => item.id !== "Select All")
      .map((item) => item.text);

    // Get selected contract types (excluding "Select All")
    const selectedContractTypes = contractTypeDataFields.contractTypeDataValue
      .filter((item) => item.id !== "Select All")
      .map((item) => item.text);

    // Apply division filter
    if (selectedDivisions.length > 0) {
      filteredData = filteredData.filter((contract) => selectedDivisions.includes(contract.division));
    }

    // Apply hospital filter
    if (selectedHospitals.length > 0) {
      filteredData = filteredData.filter((contract) => selectedHospitals.includes(contract.hospital));
    }

    // Apply contract type filter
    if (selectedContractTypes.length > 0) {
      filteredData = filteredData.filter((contract) => selectedContractTypes.includes(contract.contractType));
    }

    setFilteredContractOverviewData(filteredData);

    // Update contract type donut chart based on filtered data
    const updatedChartData = calculateContractTypeDistribution(filteredData);
    setContractChartData(updatedChartData);
  };

  const onContractManagementClick = () => {
    applyFilters();
  };

  const onViewContractDetailsClick = () => {
    history.push("/contract_management_details");
  };

  // Format date string (e.g., 2025-05-01 00:00:00) to MM/DD/YYYY
  const formatDateToMMDDYYYY = (dateString) => {
    if (!dateString || typeof dateString !== "string") return "";
    const [datePart] = dateString.split(" ");
    const parts = datePart?.split("-");
    if (!parts || parts.length !== 3) return dateString;
    const [year, month, day] = parts;
    if (!year || !month || !day) return dateString;
    return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
  };

  // Calculate unique contract types count from API responses
  const calculateUniqueContractTypesCount = (responses) => {
    const uniqueContractTypes = new Set();

    responses.forEach(({ response }) => {
      const isSuccess = response.status === true || response.status === "true";
      if (isSuccess && response.data && response.data.row_result) {
        if (Array.isArray(response.data.row_result)) {
          response.data.row_result.forEach((contractType) => {
            if (contractType.contract_type) {
              uniqueContractTypes.add(contractType.contract_type);
            }
          });
        }
      }
    });

    return uniqueContractTypes.size;
  };

  // Extract unique contract types for filter dropdown
  const extractContractTypesForFilter = (responses) => {
    const uniqueContractTypes = new Set();

    responses.forEach(({ response }) => {
      const isSuccess = response.status === true || response.status === "true";
      if (isSuccess && response.data && response.data.row_result) {
        if (Array.isArray(response.data.row_result)) {
          response.data.row_result.forEach((contractType) => {
            if (contractType.contract_type) {
              uniqueContractTypes.add(contractType.contract_type);
            }
          });
        }
      }
    });

    // Convert to array and transform to expected format for MultiSelect
    return Array.from(uniqueContractTypes).map((contractType, index) => ({
      id: index + 1,
      text: contractType,
    }));
  };

  // Calculate contract type distribution for donut chart from final overview rows
  const calculateContractTypeDistribution = (overviewRows) => {
    const contractTypeCounts = {};

    if (Array.isArray(overviewRows)) {
      overviewRows.forEach((row) => {
        const key = row.contractType || "Unknown";
        if (contractTypeCounts[key]) {
          contractTypeCounts[key] += 1;
        } else {
          contractTypeCounts[key] = 1;
        }
      });
    }

    // Define colors for different contract types
    const colorPalette = [
      "#FF6384", // Red
      "#36A2EB", // Blue
      "#FFCE56", // Yellow
      "#4BC0C0", // Teal
      "#9966FF", // Purple
      "#FF9F40", // Orange
      "#FF6384", // Red (fallback)
      "#C9CBCF", // Grey
      "#4BC0C0", // Teal (fallback)
      "#FF6384", // Red (fallback)
    ];

    // Convert to chart data format
    const chartData = Object.entries(contractTypeCounts).map(([contractType, count], index) => ({
      category: contractType,
      value: count,
      color: colorPalette[index % colorPalette.length],
    }));

    return chartData;
  };

  // Fetch Location for Provider
  const handleFetchLocationForProvider = async () => {
    try {
      setIsLocationDataLoading(true);
      setHospitalDataFields((prev) => ({ ...prev, isHospitalDataLoading: true }));

      const result = await fetchLocationForProvider(cookiesData, providerId, dispatch);

      if (result.success) {
        setLocationData(result.locationData);
        setHospitalCount(result.count);

        // Update hospital filter with real data
        setOriginalHospitalData(result.transformedData);
        setHospitalDataFields((prev) => ({
          ...prev,
          hospitalData: result.transformedData,
          isHospitalDataLoading: false,
        }));
      } else {
        console.error("Failed to fetch location for provider:", result.error);
        setLocationData([]);
        setHospitalCount(0);
        setHospitalDataFields((prev) => ({
          ...prev,
          hospitalData: [],
          isHospitalDataLoading: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching location for provider:", error);
      setLocationData([]);
      setHospitalCount(0);
      setHospitalDataFields((prev) => ({
        ...prev,
        hospitalData: [],
        isHospitalDataLoading: false,
      }));
    } finally {
      setIsLocationDataLoading(false);
    }
  };

  // Fetch contract overview data
  const fetchContractOverviewData = async () => {
    try {
      if (mountedRef.current) {
        setIsContractOverviewLoading(true);
      }

      // Fetch contract overview data using the new service
      const result = await fetchContractOverview(cookiesData, dispatch);

      // Only update state if component is still mounted
      if (mountedRef.current) {
        if (result.success) {
          // Calculate contract type distribution for donut chart from fetched overview rows
          const chartData = calculateContractTypeDistribution(result.data);
          setContractChartData(chartData);

          // Update table with fetched data
          setContractOverviewData(result.data);
          setFilteredContractOverviewData(result.data);
        } else {
          console.error("Failed to fetch contract overview data:", result.error);
          setContractOverviewData([]);
          setFilteredContractOverviewData([]);
          setContractChartData([]);
        }
      }
    } catch (error) {
      console.error("Error fetching contract overview data:", error);
      if (mountedRef.current) {
        setContractOverviewData([]);
        setFilteredContractOverviewData([]);
        setContractTypesCount(0);
        setContractChartData([]);
      }
    } finally {
      if (mountedRef.current) {
        setIsContractOverviewLoading(false);
      }
    }
  };

  // Fetch contract type data for provider
  const handleFetchContractTypeForProvider = async () => {
    try {
      if (mountedRef.current) {
        setContractTypeDataFields((prev) => ({ ...prev, isContractTypeDataLoading: true }));
      }

      const result = await fetchContractTypeForProvider(cookiesData, providerId, dispatch);

      if (mountedRef.current) {
        if (result.success) {
          setContractTypesCount(result.contractTypesForFilter.length);
          setOriginalContractTypeData(result.contractTypesForFilter);
          setContractTypeResponses(result.rawResponseData);
          setContractTypeDataFields((prev) => ({
            ...prev,
            contractTypeData: result.contractTypesForFilter,
            isContractTypeDataLoading: false,
          }));
        } else {
          console.error("Failed to fetch contract type for provider:", result.error);
          setContractTypesCount(0);
          setContractTypeResponses([]);
          setContractTypeDataFields((prev) => ({
            ...prev,
            contractTypeData: [],
            isContractTypeDataLoading: false,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching contract type data:", error);
      if (mountedRef.current) {
        setContractTypesCount(0);
        setContractTypeResponses([]);
        setContractTypeDataFields((prev) => ({
          ...prev,
          contractTypeData: [],
          isContractTypeDataLoading: false,
        }));
      }
    }
  };

  // Fetch division data
  const handleFetchDivisionData = async () => {
    try {
      if (mountedRef.current) {
        setDivisionDataFields((prev) => ({ ...prev, isDivisionDataLoading: true }));
      }

      const result = await fetchDivisionData(cookiesData, dispatch);

      if (mountedRef.current) {
        if (result.success) {
          setOriginalDivisionData(result.transformedData);
          setDivisionDataFields((prev) => ({
            ...prev,
            divisionData: result.transformedData,
            isDivisionDataLoading: false,
          }));
        } else {
          console.error("Failed to fetch division data:", result.error);
          setOriginalDivisionData([]);
          setDivisionDataFields((prev) => ({
            ...prev,
            divisionData: [],
            isDivisionDataLoading: false,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching division data:", error);
      if (mountedRef.current) {
        setOriginalDivisionData([]);
        setDivisionDataFields((prev) => ({
          ...prev,
          divisionData: [],
          isDivisionDataLoading: false,
        }));
      }
    }
  };

  // Fetch provider count
  const handleFetchProviderCount = async () => {
    try {
      setIsProviderCountLoading(true);
      const result = await fetchProviderCount(cookiesData, dispatch);

      if (mountedRef.current) {
        if (result.success) {
          setProviderCount(result.providerCount);
        } else {
          console.error("Failed to fetch provider count:", result.error);
          setProviderCount(0);
        }
      }
    } catch (error) {
      console.error("Error fetching provider count:", error);
      if (mountedRef.current) {
        setProviderCount(0);
      }
    } finally {
      if (mountedRef.current) {
        setIsProviderCountLoading(false);
      }
    }
  };

  // Fetch provider id on component mount
  useEffect(() => {
    setProviderId(fetchProviderId(cookiesData, dispatch));
  }, []);

  // Fetch location data after provider id is loaded
  useEffect(() => {
    if (providerId) {
      handleFetchLocationForProvider();
    }
  }, [providerId]);

  // Fetch contract type data independently after provider id is loaded
  useEffect(() => {
    if (providerId) {
      handleFetchContractTypeForProvider();
    }
  }, [providerId]);

  // Fetch contract overview data after provider id is loaded
  useEffect(() => {
    if (providerId && cookiesData?.out_tenant_id) {
      fetchContractOverviewData();
    }
  }, [providerId, cookiesData?.out_tenant_id]);

  // Fetch division data after tenant_id is available
  useEffect(() => {
    if (cookiesData?.out_tenant_id) {
      handleFetchDivisionData();
    }
  }, [cookiesData?.out_tenant_id]);

  // Fetch provider count after tenant_id is available
  useEffect(() => {
    if (cookiesData?.out_tenant_id) {
      handleFetchProviderCount();
    }
  }, [cookiesData?.out_tenant_id]);

  // Delay showing stats data by 1.5 seconds
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (mountedRef.current) {
        setShowStatsData(true);
      }
    }, 1500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="Main contractManagementpage">
      <div className="content">
        <div className="content-head">
          <HeaderPage pageProps={pageProps} headerConfig={headerConfig} />
        </div>
        <div className="content-body">
          <div className="Filter-links-container">
            <ul className="Filter-links">
              <div className="k-form-field-wrap-popup valueDropdown">
                <Label>Division</Label>
                <MultiSelect
                  name="divisionDataValue"
                  className="k-multiselect-wrap contract-management-multiselect"
                  dataItemKey="id"
                  textField="text"
                  data={divisionDataFields.divisionData}
                  filterable
                  filter={divisionDataFields.filterValue}
                  value={divisionDataFields.divisionDataValue}
                  autoClose={false}
                  loading={divisionDataFields.isDivisionDataLoading}
                  disabled={divisionDataFields.isDivisionDataLoading}
                  onOpen={() =>
                    onOpenChange(
                      setDivisionDataFields,
                      originalDivisionData,
                      "divisionData",
                      divisionDataFields,
                      divisionDataFields.divisionDataValue
                    )
                  }
                  onChange={(e) =>
                    handleChangeDropdownValue(
                      e,
                      "divisionDataValue",
                      setDivisionDataFields,
                      originalDivisionData,
                      divisionDataFields.divisionDataValue,
                      divisionDataFields
                    )
                  }
                  onFilterChange={(e) => filterChange(e, setDivisionDataFields, originalDivisionData, "divisionData")}
                  onBlur={() =>
                    setDivisionDataFields((prev) => ({
                      ...prev,
                      divisionData: originalDivisionData,
                      filterValue: "",
                      search: "",
                    }))
                  }
                />
              </div>
              <div className="k-form-field-wrap-popup valueDropdown">
                <Label>Hospital</Label>
                <MultiSelect
                  name="hospitalDataValue"
                  className="k-multiselect-wrap contract-management-multiselect"
                  dataItemKey="id"
                  textField="text"
                  data={hospitalDataFields.hospitalData}
                  filterable
                  filter={hospitalDataFields.filterValue}
                  value={hospitalDataFields.hospitalDataValue}
                  autoClose={false}
                  loading={hospitalDataFields.isHospitalDataLoading}
                  disabled={hospitalDataFields.isHospitalDataLoading}
                  onOpen={() =>
                    onOpenChange(
                      setHospitalDataFields,
                      originalHospitalData,
                      "hospitalData",
                      hospitalDataFields,
                      hospitalDataFields.hospitalDataValue
                    )
                  }
                  onChange={(e) =>
                    handleChangeDropdownValue(
                      e,
                      "hospitalDataValue",
                      setHospitalDataFields,
                      originalHospitalData,
                      hospitalDataFields.hospitalDataValue,
                      hospitalDataFields
                    )
                  }
                  onFilterChange={(e) => filterChange(e, setHospitalDataFields, originalHospitalData, "hospitalData")}
                  onBlur={() =>
                    setHospitalDataFields((prev) => ({
                      ...prev,
                      hospitalData: originalHospitalData,
                      filterValue: "",
                      search: "",
                    }))
                  }
                />
              </div>
              <div className="k-form-field-wrap-popup valueDropdown">
                <Label>Contract Type</Label>
                <MultiSelect
                  name="contractTypeDataValue"
                  className="k-multiselect-wrap contract-management-multiselect"
                  dataItemKey="id"
                  textField="text"
                  data={contractTypeDataFields.contractTypeData}
                  filterable
                  filter={contractTypeDataFields.filterValue}
                  value={contractTypeDataFields.contractTypeDataValue}
                  autoClose={false}
                  loading={contractTypeDataFields.isContractTypeDataLoading}
                  disabled={contractTypeDataFields.isContractTypeDataLoading}
                  onOpen={() =>
                    onOpenChange(
                      setContractTypeDataFields,
                      originalContractTypeData,
                      "contractTypeData",
                      contractTypeDataFields,
                      contractTypeDataFields.contractTypeDataValue
                    )
                  }
                  onChange={(e) =>
                    handleChangeDropdownValue(
                      e,
                      "contractTypeDataValue",
                      setContractTypeDataFields,
                      originalContractTypeData,
                      contractTypeDataFields.contractTypeDataValue,
                      contractTypeDataFields
                    )
                  }
                  onFilterChange={(e) =>
                    filterChange(e, setContractTypeDataFields, originalContractTypeData, "contractTypeData")
                  }
                  onBlur={() =>
                    setContractTypeDataFields((prev) => ({
                      ...prev,
                      contractTypeData: originalContractTypeData,
                      filterValue: "",
                      search: "",
                    }))
                  }
                />
              </div>
              <div className="button-container">
                <Tooltip anchorElement="target" position="bottom" openDelay={100}>
                  <Button
                    primary
                    className="searchbtn"
                    disabled={
                      divisionDataFields.isDivisionDataLoading ||
                      hospitalDataFields.isHospitalDataLoading ||
                      contractTypeDataFields.isContractTypeDataLoading
                    }
                    type="submit"
                    title="Update Dashboard"
                    onClick={onContractManagementClick}
                  >
                    <i className="fas fa-search"></i>
                  </Button>
                </Tooltip>
                <Tooltip anchorElement="target" position="bottom" openDelay={100}>
                  <Button primary title="View Contract Details" className="viewContractDetailsbtn" type="submit" onClick={onViewContractDetailsClick}>
                    View Contract Details <i className="fas fa-arrow-right" style={{ marginLeft: "5px" }}></i>
                  </Button>
                </Tooltip>
              </div>
            </ul>
          </div>
          <div className="contract-management-body">
            <div className="contract-management-body-left">
              <div className="contract-management-body-left-stats">
                <div className="stats-section">
                  <div className="header-label">Hospitals</div>
                  <div className="stats-value">
                    {isLocationDataLoading || !showStatsData ? (
                      <Skeleton shape="text" style={{ width: "3rem", height: "1.75rem" }} />
                    ) : (
                      hospitalCount
                    )}
                  </div>
                </div>
                <div className="stats-section">
                  <div className="header-label">Contract Types</div>
                  <div className="stats-value">
                    {contractTypeDataFields.isContractTypeDataLoading || !showStatsData ? (
                      <Skeleton shape="text" style={{ width: "3rem", height: "1.75rem" }} />
                    ) : (
                      contractTypesCount
                    )}
                  </div>
                </div>
                <div className="stats-section">
                  <div className="header-label">Providers</div>
                  <div className="stats-value">
                    {isProviderCountLoading || !showStatsData ? (
                      <Skeleton shape="text" style={{ width: "3rem", height: "1.75rem" }} />
                    ) : (
                      providerCount
                    )}
                  </div>
                </div>
              </div>
              <div className="contract-management-body-left-activity">
                <div className="activity-header">Recent Activity</div>
                <div className="activity-body">
                  <div className="development-message">
                    <div className="development-icon">
                      <div className="development-icon-circle">
                        <span className="development-icon-text">🚧</span>
                      </div>
                    </div>
                    <div className="development-content">
                      <div className="development-description">
                        We're currently working on implementing real-time activity tracking for contract management.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="contract-management-body-left-contract-chart">
                <div className="contract-chart">
                  <div className="header-label">Contract Type</div>
                  {React.useMemo(
                    () => (
                      <Chart style={{ height: "90%" }}>
                        <ChartSeries>
                          <ChartSeriesItem
                            type="donut"
                            data={contractChartData}
                            categoryField="category"
                            field="value"
                            colorField="color"
                            holeSize={40}
                            tooltip={{
                              visible: true,
                              render: ({ point, dataItem }) => {
                                const category = dataItem?.category || point?.category || "Unknown";
                                const value = dataItem?.value || point?.value || 0;
                                return (
                                  <div>
                                    <strong>{category}</strong>
                                    <br />
                                    Count: {value}
                                  </div>
                                );
                              },
                            }}
                          >
                            <ChartSeriesLabels visible={false} />
                          </ChartSeriesItem>
                        </ChartSeries>
                        <ChartLegend
                          position="right"
                          orientation="horizontal"
                          align="center"
                          width={300}
                          labels={{
                            font: "12px Arial",
                          }}
                          markers={{
                            type: "circle",
                            size: 8,
                          }}
                        />
                      </Chart>
                    ),
                    [contractChartData]
                  )}
                </div>
                {/* <div className="contract-table">
                                    <div className="header-label">
                                        Providers Contract expiring in &lt; 30 days
                                    </div>
                                    <div className="contract-table-content">
                                        <Grid
                                            data={dummyExpiringContractsData}
                                            className="expiring-contracts-grid"
                                        >
                                            <GridColumn
                                                field="employeeName"
                                                title="Employee Name"
                                                width="55%"
                                                headerCell={(props) => (
                                                    <div className="grid-header-cell">
                                                        {props.title}
                                                    </div>
                                                )}
                                            />
                                            <GridColumn
                                                field="expirationDate"
                                                title="Expiration Date"
                                                width="45%"
                                                headerCell={(props) => (
                                                    <div className="grid-header-cell">
                                                        {props.title}
                                                    </div>
                                                )}
                                            />
                                        </Grid>
                                    </div>
                                </div> */}
              </div>
            </div>
            <div className="contract-management-body-right">
              <div className="contract-management-body-right-contract-overview" style={{ position: "relative" }}>
                <div className="contract-overview-header">
                  <div>Contract Overview</div>
                  <div>
                    <Tooltip openDelay={100} position="left" anchorElement="target">
                      <Button
                        className="contract-overview-header-button"
                        onClick={() => setIsContractOverviewWindowOpen(true)}
                        title="View Detailed Contract Overview"
                      >
                        <svg
                          aria-hidden="true"
                          focusable="false"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                          className="contract-overview-header-button-icon"
                          style={{ width: "16px", height: "16px" }}
                          title="View Detailed Contract Overview"
                        >
                          <path d="M448 32H64c-17.6 0-32 14.4-32 32v288c0 17.6 14.4 32 32 32h384c17.6 0 32-14.4 32-32V64c0-17.6-14.4-32-32-32m0 319.942a1 1 0 0 1-.058.058H64.058a.5.5 0 0 1-.058-.058V64.057a1 1 0 0 1 .057-.057h383.885q.031.027.058.057zM384 480H128c35.346 0 64-28.654 64-64h128c0 35.346 28.654 64 64 64m32-160H96c58.263-113.973 183.21-192 320-192z"></path>
                        </svg>
                      </Button>
                    </Tooltip>
                  </div>
                </div>
                {isContractOverviewLoading ? (
                  <LoadingPanel />
                ) : (
                  <>
                    <div className="contract-overview-body">
                      {filteredContractOverviewData ? (
                        <Grid data={filteredContractOverviewData} className="contract-overview-grid">
                          {/* <GridColumn
                                                            field="action"
                                                            title="Action"
                                                            width="10%"
                                                            cell={(props) => (
                                                                <div className="action-cell">
                                                                    <span
                                                                        className="k-icon k-font-icon k-i-hyperlink-open action-icon k-table-td"
                                                                        title="View Details"
                                                                        onClick={() => handleActionClick(props.dataItem)}
                                                                    />
                                                                </div>
                                                            )}
                                                            headerCell={(props) => (
                                                                <div className="grid-header-cell">
                                                                    {props.title}
                                                                </div>
                                                            )}
                                                        /> */}
                          <GridColumn
                            field="division"
                            title="Division"
                            width="10%"
                            headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                          />
                          <GridColumn
                            field="hospital"
                            title="Hospital"
                            width="18%"
                            headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                          />
                          <GridColumn
                            field="contractType"
                            title="Contract Type"
                            width="18%"
                            headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                          />
                          <GridColumn
                            field="providerCount"
                            title="Providers"
                            width="10%"
                            headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                          />
                        </Grid>
                      ) : (
                        <></>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {isContractOverviewWindowOpen && (
        <Window
          title="Contract Overview"
          onClose={() => setIsContractOverviewWindowOpen(false)}
          width={window.innerWidth * 0.9}
          height={window.innerHeight * 0.8}
          resizable={true}
          modal={true}
        >
          <div className="contract-overview-window-content">
            {isContractOverviewLoading ? (
              <LoadingPanel />
            ) : filteredContractOverviewData ? (
              <Grid data={filteredContractOverviewData} className="contract-overview-grid">
                {/* <GridColumn
                                        field="action"
                                        title="Action"
                                        width="10%"
                                        cell={(props) => (
                                            <div className="action-cell">
                                                <span
                                                    className="k-icon k-font-icon k-i-hyperlink-open action-icon k-table-td"
                                                    title="View Details"
                                                    onClick={() => handleActionClick(props.dataItem)}
                                                />
                                            </div>
                                        )}
                                        headerCell={(props) => (
                                            <div className="grid-header-cell">
                                                {props.title}
                                            </div>
                                        )}
                                    /> */}
                <GridColumn
                  field="division"
                  title="Division"
                  width="8%"
                  headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                />
                <GridColumn
                  field="hospital"
                  title="Hospital"
                  width="12%"
                  headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                />
                <GridColumn
                  field="contractType"
                  title="Contract Type"
                  width="12%"
                  headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                />
                <GridColumn
                  field="providerCount"
                  title="Providers"
                  width="8%"
                  headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                />
                <GridColumn
                  field="startDate"
                  title="Start Date"
                  width="8%"
                  cell={(props) => <td>{formatDateToMMDDYYYY(props.dataItem.startDate)}</td>}
                  headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                />
                <GridColumn
                  field="endDate"
                  title="End Date"
                  width="8%"
                  cell={(props) => <td>{formatDateToMMDDYYYY(props.dataItem.endDate)}</td>}
                  headerCell={(props) => <div className="grid-header-cell">{props.title}</div>}
                />
              </Grid>
            ) : (
              <></>
            )}
          </div>
        </Window>
      )}
    </div>
  );
};

export default ContractManagement;
